<template>
  <div class="dsd-root sc-page-root">
    <FiPageRail title="Risk Matrix" nowrap-actions>
      <template #actions>
        <driver-manager-risk-matrix-help />
      </template>
    </FiPageRail>

    <!-- toolbar kept in sync with the other Driver Manager pages -->
    <div class="sc-toolbar">
      <div class="sc-toolbar-group dsd-date-presets" role="group" aria-label="Date range">
        <button v-for="m in quickRangeOptions" :key="'qr-' + m" type="button"
          :class="['sc-chip', { 'sc-chip--active': activeQuickRangeMonths === m }]"
          :aria-pressed="activeQuickRangeMonths === m ? 'true' : 'false'"
          @click="setDatePreset(m)">{{ m }}m</button>
      </div>
      <div class="dsd-date-fields">
        <date-picker class="sc-control dsd-date-field" v-model="details_fromDate" :config="optionsFrom" :placeholder="details_fromDate"/>
        <span class="dsd-date-sep">to</span>
        <date-picker class="sc-control dsd-date-field" v-model="details_toDate" :config="optionsTo" :placeholder="details_toDate"/>
      </div>
    </div>

    <div v-if="loading && !drivers.length && !error" style="text-align: center; margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
    <div v-else-if="error" class="text-center text-danger" style="margin: 50px;">
      <p>{{ error }}</p>
      <b-button size="sm" variant="outline-secondary" @click="load_data()">Retry</b-button>
    </div>
    <div v-else-if="!drivers.length" class="text-center text-muted" style="margin: 50px;">
      <h4>No pre-calculated data for this period.</h4>
      <p>Try a different date range.</p>
    </div>
    <div v-else-if="!exposure.rows.length" class="text-center text-muted" style="margin: 50px;">
      <h4>No driver has a scored, mileage-bearing scorecard this period.</h4>
      <p>This page needs a combined score and mileage from the scorecard.</p>
    </div>

    <div v-else :class="{ 'dsd-stale': loading }">
      <!-- the summary leads: the allocation gap is the finding, the rest sizes it -->
      <div class="ex-stats">
        <div class="oda-card ex-stat" :style="{ '--oda-accent': gapColor }">
          <div class="oda-card-header">
            <span class="oda-card-title">Who is getting the miles</span>
          </div>
          <div class="oda-card-body">
            <div class="ex-big ex-verdict" :style="{ color: gapColor }">{{ gapHeadline }}</div>
            <div class="ex-sub">
              the average <b>mile</b> is driven by someone scoring <b>{{ fleet.mileWeightedScore }}</b>
              &middot; the average <b>driver</b> scores <b>{{ fleet.simpleAvgScore }}</b>
            </div>
            <p class="ex-note">{{ gapVerdict }}</p>
            <button type="button" class="ex-showme" @click="drill = 'gap'">Show me the numbers</button>
          </div>
        </div>

        <div class="oda-card ex-stat" :style="{ '--oda-accent': quartileColor }">
          <div class="oda-card-header">
            <span class="oda-card-title">Your {{ fleet.quartileSize }} lowest-scoring drivers</span>
          </div>
          <div class="oda-card-body">
            <div class="ex-big" :style="{ color: quartileColor }">{{ fleet.worstQuartileShare }}<span class="ex-unit">%</span></div>
            <div class="ex-sub">
              of every mile the fleet drove &mdash; and they are a quarter of your
              {{ fleet.drivers }} drivers
            </div>
            <p class="ex-note">{{ quartileVerdict }}</p>
            <button type="button" class="ex-showme" @click="drill = 'quartile'">Show me the numbers</button>
          </div>
        </div>

        <div class="oda-card ex-stat" :style="{ '--oda-accent': overColor }">
          <div class="oda-card-header">
            <span class="oda-card-title">Driving more than their score supports</span>
          </div>
          <div class="oda-card-body">
            <div class="ex-big" :style="{ color: overColor }">{{ fleet.overCount }}</div>
            <div class="ex-sub">
              {{ fleet.overCount === 1 ? 'driver' : 'drivers' }} &middot;
              {{ fmtInt(fleet.excessMiles) }} extra miles between them
            </div>
            <p class="ex-note">
              Below the fleet average on combined score <em>and</em> carrying more miles than that
              score supports. A strong driver with heavy miles is not counted here &mdash; that is
              dispatch doing its job.
            </p>
            <button type="button" class="ex-showme" @click="drill = 'over'">Show me the numbers</button>
          </div>
        </div>

        <div class="oda-card ex-stat" :style="{ '--oda-accent': belowColor }">
          <div class="oda-card-header">
            <span class="oda-card-title">Miles on below-average drivers</span>
          </div>
          <div class="oda-card-body">
            <div class="ex-big" :style="{ color: belowColor }">{{ fleet.belowAvgShare }}<span class="ex-unit">%</span></div>
            <div class="ex-sub">
              {{ fmtInt(fleet.belowAvgMiles) }} miles across {{ fleet.belowAvgCount }} drivers
            </div>
            <p class="ex-note">
              The plainest statement of what is at stake: the share of the fleet&rsquo;s road being
              covered by anyone scoring under {{ fleet.simpleAvgScore }}.
            </p>
            <button type="button" class="ex-showme" @click="drill = 'below'">Show me the numbers</button>
          </div>
        </div>
      </div>

      <!-- "show me": every headline number opens the rows it was computed
           from. a stat you cannot audit is a stat people quietly stop
           believing, and these are all small tables -- there is no reason to
           make anyone take them on faith. -->
      <b-modal :visible="!!drill" :title="drillTitle" size="lg" ok-only scrollable
               @hide="drill = null">
        <p class="ex-drill-intro">{{ drillIntro }}</p>
        <div class="ex-drill-wrap">
          <table class="ex-drill">
            <thead>
              <tr>
                <th>Driver</th>
                <th class="num">Miles</th>
                <th class="num">Score</th>
                <th v-if="drillShowsShare" class="num">Score supports</th>
                <th v-if="drillShowsShare" class="num">Difference</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in drillRows" :key="d.driver_id" @click="openDriver(d)">
                <td>{{ d.driver_name }}</td>
                <td class="num">{{ fmtInt(d.miles) }}</td>
                <td class="num" :class="{ 'ex-over-rate': d.score < fleet.simpleAvgScore }">{{ d.score }}</td>
                <td v-if="drillShowsShare" class="num">{{ fmtInt(Math.round(d.mark)) }}</td>
                <td v-if="drillShowsShare" class="num" :class="d.delta > 0 ? 'ex-pos' : 'ex-neg'">
                  {{ d.delta > 0 ? '+' : '' }}{{ fmtInt(d.delta) }}
                </td>
              </tr>
            </tbody>
            <tfoot v-if="drillFooter">
              <tr>
                <td><b>{{ drillFooter.label }}</b></td>
                <td class="num"><b>{{ fmtInt(drillFooter.miles) }}</b></td>
                <td class="num"><b>{{ drillFooter.score }}</b></td>
                <td v-if="drillShowsShare" class="num"></td>
                <td v-if="drillShowsShare" class="num"><b>{{ drillFooter.delta }}</b></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p class="ex-drill-note">{{ drillNote }}</p>
      </b-modal>

      <div class="oda-card ex-chart-card">
        <div class="oda-card-header">
          <span class="oda-card-title"><i class="fa fa-crosshairs" aria-hidden="true"></i> Who is getting the miles</span>
          <span class="ex-hint">colour = quadrant · click to open a driver</span>
        </div>
        <div class="oda-card-body">
          <div class="ex-canvas-wrap"><canvas ref="chExposure"></canvas></div>
        </div>
      </div>

      <div class="oda-card ex-bars-card">
        <div class="oda-card-header">
          <span class="oda-card-title"><i class="fa fa-tasks" aria-hidden="true"></i> Miles driven vs miles their score supports</span>
          <div class="ex-bar-filters" role="group" aria-label="Filter by dispatch status">
            <button v-for="opt in barStatusOptions" :key="opt.value" type="button"
              :class="['sc-chip', { 'sc-chip--active': barStatus === opt.value }]"
              :aria-pressed="barStatus === opt.value ? 'true' : 'false'"
              :disabled="!opt.count && opt.value"
              @click="barStatus = opt.value">
              {{ opt.label }} <span class="ex-chip-count">{{ opt.count }}</span>
            </button>
          </div>
        </div>
        <div class="oda-card-body">
          <p class="ex-bars-intro">
            Each bar is the miles a driver actually ran. The <span class="ex-tick-inline"></span> mark is what
            they would run if mileage tracked their combined score — stronger drivers more, weaker
            drivers less. <b>A bar reaching past the mark means more miles than their score supports.</b>
          </p>
          <div class="ex-bar-head">
            <div>Driver</div>
            <div>Miles driven — the mark is what their score supports</div>
            <div class="ex-bar-miles">Miles</div>
            <div class="ex-bar-score">Score</div>
            <div class="ex-bar-delta">vs mark</div>
            <div></div>
          </div>
          <div class="ex-bars">
            <div v-for="d in barRows" :key="d.driver_id" class="ex-bar-row" @click="openDriver(d)">
              <div class="ex-bar-name">{{ d.driver_name }}</div>
              <div class="ex-bar-track">
                <div class="ex-bar-fill" :style="{ width: barPct(d.miles) + '%', background: barColor(d) }"></div>
                <div class="ex-bar-tick" :style="{ left: barPct(d.fairShare) + '%' }" title="miles their record supports"></div>
              </div>
              <div class="ex-bar-miles">{{ fmtInt(d.miles) }}</div>
              <div class="ex-bar-score" :class="{ 'ex-over-rate': d.score < fleet.simpleAvgScore }">{{ d.score }}</div>
              <div class="ex-bar-delta" :class="d.delta > 0 ? 'ex-pos' : 'ex-neg'">{{ d.delta > 0 ? '+' : '' }}{{ fmtInt(d.delta) }}</div>
              <div class="ex-bar-flag">
                <span v-if="d.status === 'over'" class="ex-chip ex-chip--over">Over-dispatched</span>
                <span v-else-if="d.status === 'under'" class="ex-chip ex-chip--under">Room to give</span>
              </div>
            </div>
          </div>
          <p v-if="!barRows.length" class="ex-bars-empty">
            No driver in this period is {{ barStatusLabel }}.
          </p>
          <p class="ex-legend-note">
            Being past the mark is not on its own a problem — somebody has to drive the miles. It only counts
            as <b>over-dispatched</b> when the driver is <em>also</em> below the fleet average score.
            A strong driver running long is dispatch working, not a finding.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import { Chart, registerables } from 'chart.js'
import { justDate } from '../helpers/dates'
import { get_all_driver_scorecards } from '../networking/tbn_apis'
import { cm_get_all_driver_scorecards } from '../networking/apis'
import {
  QUICK_RANGE_MONTHS,
  activeQuickRangeMonths,
  defaultScorecardRange,
  normalizeRange,
  quickRangeDates,
  scorecardPickerOptions,
} from '../utils/driver_manager_dates'
import dtPickerPortal from '../mixins/dtpicker_portal_mixin'
import {
  hexAlpha, quadrantGuidesPlugin, matrixGuideLineColor,
  MATRIX_LEGEND_FONT, MATRIX_AXIS_TITLE_FONT, MATRIX_LEGEND_PADDING, MATRIX_LEGEND_BOX_PADDING,
  MATRIX_AXIS_TITLE_PADDING, bubbleLayoutPadding,
} from '../utils/erm_charts'
import {
  buildExposure, exposureBubbleRadius, exposureQuadrantColor,
  EXPOSURE_QUADRANT_ORDER, EXPOSURE_CORNER_LABELS, EXPOSURE_CORNER_QUADRANTS,
} from '../utils/risk_matrix'
import DriverManagerRiskMatrixHelp from './driver_manager_risk_matrix_help.vue'

Chart.register(...registerables)

// same presets as the other Driver Manager pages; 12 is the server-side cap

// The riskiest-drivers card reads as a caution whatever the number says: the
// subject is the worst records on the roster, so a green card there invites a
// glance-read of "all clear" about the very group you keep an eye on. The soft
// tone is the resting state; it hardens once the group is actually carrying
// more than an even share of the miles, which is the line that matters.
const DANGER_SOFT = { light: '#D1706A', dark: '#E9A49E' }
const DANGER_STRONG = { light: '#C1352F', dark: '#E4736C' }

export default {
  name: 'driver_manager_risk_matrix',

  mixins: [dtPickerPortal],

  components: { DriverManagerRiskMatrixHelp },

  data() {
    return {
      loading: false,
      error: null,
      // reactive: the stat cards bind accent colours in the template, before
      // any chart exists to resolve a theme from
      midnight: false,
      drivers: [],
      // which headline stat has its "show me" table open, if any
      drill: null,
      // dispatch-status filter on the miles table; '' is everyone
      barStatus: '',
      details_fromDate: justDate(defaultScorecardRange().from),
      details_toDate: justDate(defaultScorecardRange().to),
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    isCmTenant() {
      return (this.user && this.user.types && this.user.types[this.user.tenant]) === 'CM'
    },
    exposure() {
      return buildExposure({ drivers: this.drivers })
    },
    fleet() {
      return this.exposure.fleet
    },
    // A POSITIVE gap means the mile-weighted score BEATS the simple average --
    // the road is in stronger hands than the roster alone suggests. Note the
    // polarity is the opposite of the at-fault-rate version this replaced,
    // where a positive gap was the bad case.
    gapColor() {
      if (!this.fleet.drivers) return '#adb5bd'
      return this.fleet.scoreGap < 0 ? '#C1352F' : '#1E7B3C'
    },
    gapHeadline() {
      if (!this.fleet.drivers) return '—'
      const g = this.fleet.scoreGap
      if (g < 0) return 'Weaker drivers'
      if (g > 0) return 'Stronger drivers'
      return 'Evenly spread'
    },
    gapVerdict() {
      const f = this.fleet
      if (!f.drivers) return ''
      if (f.scoreGap < 0) {
        return 'Your busier drivers score lower than your quieter ones, so the fleet is carrying '
          + 'more risk than the roster alone suggests. No individual scorecard shows this — it '
          + 'belongs to the schedule, not to any one driver.'
      }
      if (f.scoreGap > 0) {
        return 'Your busier drivers score higher than your quieter ones, so the road is in stronger '
          + 'hands than the roster alone suggests. That is the schedule working in your favour.'
      }
      return 'Miles are spread evenly across strong and weak scores alike.'
    },
    quartileVerdict() {
      const f = this.fleet
      if (!f.drivers) return ''
      const share = f.worstQuartileShare
      const base = 'A quarter of the drivers would normally do about a quarter of the driving, '
        + 'so 25% is the neutral mark. '
      if (share > 27) {
        return base + `At ${share}% they are doing more than their share — the heavy mileage is `
          + 'landing on your weakest scores.'
      }
      if (share < 23) {
        return base + `At ${share}% they are doing less than their share — the heavy mileage is `
          + 'going to stronger drivers, which is what you want.'
      }
      return base + `At ${share}% they are carrying about their share, no more and no less.`
    },
    quartileColor() {
      const tone = this.fleet.worstQuartileShare > 25 ? DANGER_STRONG : DANGER_SOFT
      return this.midnight ? tone.dark : tone.light
    },
    overColor() {
      return this.fleet.overCount ? '#B07D08' : '#1E7B3C'
    },
    // the share of the road covered by anyone under the average; reads as a
    // caution at rest and hardens once it passes half the fleet's mileage
    belowColor() {
      const tone = this.fleet.belowAvgShare > 50 ? DANGER_STRONG : DANGER_SOFT
      return this.midnight ? tone.dark : tone.light
    },
    drillRows() {
      const rows = this.exposure.rows
      if (this.drill === 'quartile') {
        return rows.slice().sort((a, b) => a.score - b.score).slice(0, this.fleet.quartileSize)
      }
      if (this.drill === 'over') {
        return rows.filter(d => d.status === 'over').sort((a, b) => b.delta - a.delta)
      }
      if (this.drill === 'below') {
        return rows.filter(d => d.score < this.fleet.simpleAvgScore).sort((a, b) => b.miles - a.miles)
      }
      return rows.slice().sort((a, b) => b.miles - a.miles)
    },
    drillShowsShare() {
      return this.drill === 'over'
    },
    drillTitle() {
      return {
        gap: 'Who is getting the miles',
        quartile: `Your ${this.fleet.quartileSize} lowest-scoring drivers`,
        over: 'Driving more than their score supports',
        below: 'Miles on below-average drivers',
      }[this.drill] || ''
    },
    drillIntro() {
      const f = this.fleet
      return {
        gap: `Every scored driver this period, heaviest first. Average the score column one driver `
          + `at a time and you get ${f.simpleAvgScore}. Weight each driver by their miles and you `
          + `get ${f.mileWeightedScore} — that is the whole comparison.`,
        quartile: `The quarter of drivers with the lowest combined scores. Between them they ran `
          + `${this.fmtInt(this.drillMiles)} of the fleet's ${this.fmtInt(f.totalMiles)} miles.`,
        over: `Drivers below the fleet average of ${f.simpleAvgScore} who also ran more miles than `
          + `their score supports. Both tests have to fail before anyone appears here.`,
        below: `Everyone scoring under the fleet average of ${f.simpleAvgScore}, heaviest first.`,
      }[this.drill] || ''
    },
    drillNote() {
      return {
        gap: 'Click any row to open that driver. Drivers with no combined score, and those in the '
          + 'Insufficient Data tier, are not listed.',
        quartile: 'If every driver drove the same distance, this group would hold 25% of the miles.',
        over: '“Score supports” is their own mileage scaled by how their score compares to the '
          + 'fleet average — above average pushes it up, below average pulls it down.',
        below: 'Not a finding on its own: half a roster sits below any average. It is the share of '
          + 'MILES they carry that decides whether it matters.',
      }[this.drill] || ''
    },
    drillMiles() {
      return this.drillRows.reduce((s, d) => s + d.miles, 0)
    },
    drillFooter() {
      const rows = this.drillRows
      if (!rows.length) return null
      const miles = this.drillMiles
      return {
        label: this.drill === 'gap' ? 'Whole fleet' : 'These drivers',
        miles,
        // mile-weighted, matching how the headline computes it
        score: miles
          ? Math.round(rows.reduce((s, d) => s + d.score * d.miles, 0) / miles * 10) / 10
          : 0,
        delta: this.fmtInt(rows.reduce((s, d) => s + d.delta, 0)),
      }
    },
    // counts come from the unfiltered set so a chip still shows its size after
    // you have narrowed to a different one
    barStatusOptions() {
      const rows = this.exposure.rows
      const n = v => rows.filter(d => d.status === v).length
      return [
        { value: '', label: 'All', count: rows.length },
        { value: 'over', label: 'Over-dispatched', count: n('over') },
        { value: 'under', label: 'Room to give', count: n('under') },
        { value: 'inline', label: 'In line', count: n('inline') },
      ]
    },
    barRows() {
      if (!this.barStatus) return this.exposure.rows
      return this.exposure.rows.filter(d => d.status === this.barStatus)
    },
    barStatusLabel() {
      const opt = this.barStatusOptions.find(o => o.value === this.barStatus)
      return opt ? opt.label.toLowerCase() : ''
    },
    // deliberately the whole fleet, not barRows: rescaling to the filtered
    // subset would make the same driver's bar change length depending on which
    // chip is active, which reads as the data moving
    maxBarMiles() {
      return this.exposure.rows.reduce((m, d) => Math.max(m, d.miles, d.fairShare), 0) || 1
    },
    quickRangeOptions() { return QUICK_RANGE_MONTHS },
    activeQuickRangeMonths() {
      return activeQuickRangeMonths(
        { from: this.details_fromDate, to: this.details_toDate })
    },
    optionsFrom() {
      return scorecardPickerOptions('from')
    },
    optionsTo() {
      return scorecardPickerOptions('to')
    },
  },

  watch: {
    details_toDate() {
      const fixed = normalizeRange(
        { from: this.details_fromDate, to: this.details_toDate },
        { changed: 'to' })
      // null = unparseable (cleared or half-typed): leave it alone and do not
      // reload, or "Invalid date" reaches the api as a month string
      if (!fixed) return
      if (fixed.from !== this.details_fromDate) this.details_fromDate = fixed.from
      if (fixed.to !== this.details_toDate) this.details_toDate = fixed.to
      this.scheduleRangeLoad()
    },
    details_fromDate() {
      const fixed = normalizeRange(
        { from: this.details_fromDate, to: this.details_toDate },
        { changed: 'from' })
      // null = unparseable (cleared or half-typed): leave it alone and do not
      // reload, or "Invalid date" reaches the api as a month string
      if (!fixed) return
      if (fixed.from !== this.details_fromDate) this.details_fromDate = fixed.from
      if (fixed.to !== this.details_toDate) this.details_toDate = fixed.to
      this.scheduleRangeLoad()
    },
  },

  created() {
    this.midnight = document.documentElement.getAttribute('data-fi-theme') === 'midnight'
    this.load_data()
  },

  beforeDestroy() {
    if (this._chart) { this._chart.destroy(); this._chart = null }
  },

  methods: {
    // a chip writes its range into the pickers, which are always visible and
    // show exactly the days the page reads
    setDatePreset(months) {
      const { from, to } = quickRangeDates(months)
      this.details_fromDate = from
      this.details_toDate = to
    },

    scheduleRangeLoad() {
      if (this._rangeLoadScheduled) return
      this._rangeLoadScheduled = true
      this.$nextTick(() => {
        this._rangeLoadScheduled = false
        this.load_data()
      })
    },

    async load_data() {
      const seq = (this._loadSeq = (this._loadSeq || 0) + 1)
      this.loading = true
      this.error = null
      try {
        const start_month = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const end_month = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const scorecardRes = this.isCmTenant
          ? await cm_get_all_driver_scorecards(this.user.tenant, start_month, end_month)
          : await get_all_driver_scorecards(this.user.tenant, start_month, end_month)
        if (seq !== this._loadSeq) return

        const scorecard = this.unwrap(scorecardRes)
        if (scorecard && scorecard.error) { this.error = scorecard.error; this.drivers = []; return }
        if (!scorecard) { this.error = 'Unexpected response format from server'; return }
        this.drivers = (scorecard.drivers || []).map(d => this.normalizeDriverRow(d))

      } catch (e) {
        if (seq !== this._loadSeq) return
        const serverMsg = (e.response && e.response.data && e.response.data.error)
          || (e.response && e.response.data && e.response.data.data && e.response.data.data.error)
        this.error = serverMsg || e.message || 'Error loading risk matrix data'
      } finally {
        if (seq === this._loadSeq) {
          this.loading = false
          this.$nextTick(() => this.buildChart())
        }
      }
    },

    unwrap(response) {
      return (response && response.value && response.value.data) || (response && response.data) || null
    },

    // CM returns miles_driven where TBN returns total_miles
    normalizeDriverRow(row) {
      if (!row) return row
      if (this.isCmTenant && row.total_miles == null && row.miles_driven != null) {
        return { ...row, total_miles: row.miles_driven }
      }
      return row
    },

    fmtInt(n) { return (Number(n) || 0).toLocaleString() },


    barPct(v) {
      return Math.min(100, (Number(v) || 0) / this.maxBarMiles * 100)
    },

    barColor(d) {
      return exposureQuadrantColor(d.quadrant, this.midnight)
    },

    openDriver(d) {
      this.$router.push({ name: 'driver_manager_individual', query: { driver_id: d.driver_id } })
    },

    applyChartTheme() {
      const midnight = document.documentElement.getAttribute('data-fi-theme') === 'midnight'
      this.midnight = midnight
      const rootStyles = getComputedStyle(document.documentElement)
      const mutedInk = rootStyles.getPropertyValue('--fi-text-muted').trim()
      const gridLine = rootStyles.getPropertyValue('--fi-line').trim()
      Chart.defaults.color = mutedInk || (midnight ? '#c7d2dd' : '#666')
      Chart.defaults.borderColor = gridLine || (midnight ? 'rgba(255, 255, 255, 0.09)' : 'rgba(0, 0, 0, 0.1)')
      Chart.defaults.font.family = getComputedStyle(document.body).fontFamily || Chart.defaults.font.family
    },

    buildChart() {
      const canvas = this.$refs.chExposure
      if (!canvas || !this.exposure.rows.length) return
      this.applyChartTheme()
      const existing = Chart.getChart(canvas)
      if (existing) existing.destroy()

      const qc = q => exposureQuadrantColor(q, this.midnight)
      const maxBubble = exposureBubbleRadius()
      const datasets = EXPOSURE_QUADRANT_ORDER.map(q => ({
        label: q,
        data: this.exposure.quadrants[q].map(d => ({
          x: d.score, y: d.miles, r: exposureBubbleRadius(), _d: d,
        })),
        backgroundColor: hexAlpha(qc(q), 0.55),
        borderColor: qc(q),
        borderWidth: 1,
        // a dot at score 100 or at zero miles sits ON an axis bound; without
        // this chart.js slices it in half
        clip: false,
      }))

      const labelColors = {}
      Object.keys(EXPOSURE_CORNER_QUADRANTS).forEach(k => {
        labelColors[k] = qc(EXPOSURE_CORNER_QUADRANTS[k])
      })

      this._chart = new Chart(canvas.getContext('2d'), {
        type: 'bubble',
        data: { datasets },
        plugins: [quadrantGuidesPlugin],
        options: {
          maintainAspectRatio: false,
          layout: { padding: bubbleLayoutPadding(maxBubble) },
          onClick: (evt, elements) => {
            if (!elements || !elements.length) return
            const el = elements[0]
            const point = this._chart.data.datasets[el.datasetIndex].data[el.index]
            if (point && point._d) this.openDriver(point._d)
          },
          onHover: (evt, elements) => {
            evt.native.target.style.cursor = elements && elements.length ? 'pointer' : 'default'
          },
          scales: {
            x: {
              min: 0,
              max: 100,
              title: {
                display: true,
                text: 'Combined score (higher is better) →',
                font: MATRIX_AXIS_TITLE_FONT,
                padding: MATRIX_AXIS_TITLE_PADDING,
              },
            },
            y: {
              min: 0,
              title: {
                display: true,
                text: 'Miles driven in the period →',
                font: MATRIX_AXIS_TITLE_FONT,
                padding: MATRIX_AXIS_TITLE_PADDING,
              },
            },
          },
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                usePointStyle: true,
                boxWidth: 8,
                boxPadding: MATRIX_LEGEND_BOX_PADDING,
                font: MATRIX_LEGEND_FONT,
                padding: MATRIX_LEGEND_PADDING,
              },
            },
            rmQuadrantGuides: {
              xMid: this.fleet.simpleAvgScore,
              yMid: this.fleet.avgMiles,
              lineColor: matrixGuideLineColor(this.midnight),
              fontFamily: Chart.defaults.font.family,
              labels: EXPOSURE_CORNER_LABELS,
              labelColors,
            },
            tooltip: {
              callbacks: {
                label: ctx => {
                  const d = ctx.raw && ctx.raw._d
                  if (!d) return ''
                  return [
                    `${d.driver_name} — ${d.quadrant}`,
                    `combined score ${d.score} · ${this.fmtInt(d.miles)} mi`,
                    `score supports ${this.fmtInt(Math.round(d.mark))} mi`,
                  ]
                },
              },
            },
          },
        },
      })
    },
  },
}
</script>
<style scoped>
/* ── shared Driver Manager chrome ────────────────────────────────────────
   copied from driver_manager_risk_matrix.vue rather than imported: these are
   scoped blocks, so they don't cross components, and the four Driver Manager
   pages each carry their own copy (the roster list says as much above its
   own). .sc-page-root / .sc-toolbar / .sc-toolbar-group / .sc-control are
   NOT here -- those are global in assets/css/driver-manager-mobile.css. */

/* date preset chips (od-chip recipe, token-native so midnight needs no
   overrides) */
.dsd-date-presets {
  gap: 6px;
}
.sc-chip {
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--fi-text-muted, #4b5563);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
}
/* :not(.sc-chip--active) is load-bearing: .sc-chip:hover out-specifies
   .sc-chip--active (a pseudo-class beats nothing at the same class count), so a
   bare hover rule repainted the SELECTED chip near-white while its own
   color: #fff survived -- white on white for as long as the pointer stayed on
   the chip you just clicked, in every theme. */
.sc-chip:not(.sc-chip--active):hover {
  background: var(--fi-surface-sunken, #eef4fb);
}
.sc-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}
.sc-chip--active:hover {
  background: var(--fi-ops-accent-strong, #305b80);
  border-color: var(--fi-ops-accent-strong, #305b80);
}
.dsd-date-fields {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dsd-date-field {
  width: 122px;
  font-size: 0.9rem;
}
/* ios safari zooms the page on focusing any input under 16px */
@media (max-width: 768px) {
  .dsd-date-field {
    font-size: 16px;
  }
}
.dsd-date-sep {
  color: var(--fi-text-muted, #6c757d);
  font-size: 0.85rem;
}
/* reloads keep the previous render visible at reduced opacity instead of
   flashing back to a spinner (stale-hold, ops_desk_analytics precedent) */
.dsd-stale {
  opacity: 0.55;
  transition: opacity 0.15s;
}


/* ── ERM Dashboard card language ─────────────────────────────────────────
   copied from ops_desk_analytics.vue for the same reason the toolbar block
   above is copied from the roster list: both are scoped, so neither crosses
   components. one --oda-accent per card drives the border, the two gradient
   rails and the header/body tints, which is what lets a quadrant card be
   re-hued by setting a single variable inline.

   the accent defaults to the ops family's own colour, because half this
   page's data IS the ERM -- the outcome axis is ops events. */
.oda-card {
  --oda-accent: var(--fi-ops-accent, #3a6e99);
}
.oda-card {
  display: flex;
  flex-direction: column;
  background: var(--fi-surface, #fff);
  border: 2px solid color-mix(in srgb, var(--oda-accent) 28%, var(--fi-line));
  border-radius: 16px;
  box-shadow:
    0 4px 22px color-mix(in srgb, var(--oda-accent) 14%, transparent),
    0 1px 4px rgba(20, 40, 60, 0.06);
  position: relative;
  overflow: hidden;
}
.oda-card::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 6px;
  background: linear-gradient(
    90deg,
    var(--oda-accent),
    color-mix(in srgb, var(--oda-accent) 55%, white)
  );
  z-index: 3;
}
.oda-card::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: linear-gradient(
    180deg,
    var(--oda-accent),
    color-mix(in srgb, var(--oda-accent) 70%, white)
  );
  z-index: 1;
}
.oda-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 13px 16px 11px 20px;
  background: color-mix(in srgb, var(--oda-accent) 14%, var(--fi-surface));
  border-bottom: 2px solid color-mix(in srgb, var(--oda-accent) 22%, var(--fi-line));
  position: relative;
  z-index: 2;
}
.oda-card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 15px;
  font-weight: 700;
  color: color-mix(in srgb, var(--oda-accent) 65%, var(--fi-text));
}
.oda-card-title .fa {
  color: var(--oda-accent);
}
.oda-card-body {
  flex: 1;
  padding: 12px 14px 10px 18px;
  background: color-mix(in srgb, var(--oda-accent) 6%, var(--fi-surface));
  position: relative;
  z-index: 2;
}
.oda-card-empty {
  text-align: center;
  color: var(--fi-text-muted, #8a97a5);
  padding: 48px 0;
}

/* ── exposure ────────────────────────────────────────────────────────────
   card chrome comes from the .oda-* block above; these rules cover what sits
   inside a card body, plus the page-level grid. */

.ex-warn {
  margin-bottom: 16px;
  --oda-accent: #b8860b;
}
.ex-warn .oda-card-title { font-size: 13.5px; font-weight: 600; }

.ex-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
  align-items: stretch;
}

.ex-big {
  font-size: 30px;
  font-weight: 700;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}
.ex-unit { font-size: 17px; font-weight: 600; margin-left: 1px; }
/* the headline card states a verdict rather than a figure, so it needs to sit
   on the same baseline as the numbers beside it without shouting */
.ex-verdict { font-size: 23px; line-height: 1.25; }
.ex-sub {
  font-size: 11.5px;
  color: var(--fi-text-muted, #6c757d);
  margin-top: 3px;
}
.ex-note {
  font-size: 11.5px;
  color: var(--fi-text-muted, #6c757d);
  margin: 9px 0 0;
}

/* a quiet affordance: present on every stat, but never competing with the
   number it sits under */
.ex-showme {
  background: none;
  border: none;
  padding: 6px 0 0;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--fi-link, #007bff);
  cursor: pointer;
}
.ex-showme:hover { text-decoration: underline; }
.ex-showme::after { content: ' →'; }

.ex-drill-intro { font-size: 13.5px; margin-bottom: 14px; }
.ex-drill-note {
  font-size: 12px;
  color: var(--fi-text-muted, #6c757d);
  margin: 12px 0 0;
}
.ex-drill-wrap { overflow-x: auto; }
table.ex-drill {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 520px;
}
table.ex-drill th {
  text-align: left;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--fi-text-muted, #6c757d);
  border-bottom: 1px solid var(--fi-line, #dee2e6);
  padding: 6px 10px;
}
table.ex-drill td {
  padding: 6px 10px;
  border-bottom: 1px solid var(--fi-line-soft, #eef2f6);
}
table.ex-drill tbody tr { cursor: pointer; }
table.ex-drill tbody tr:hover { background: var(--fi-surface-sunken, #f1f5f9); }
table.ex-drill .num {
  text-align: right;
  font-family: var(--fi-font-mono, monospace);
  font-variant-numeric: tabular-nums;
}
table.ex-drill tfoot td {
  border-top: 2px solid var(--fi-line, #dee2e6);
  border-bottom: none;
  background: var(--fi-surface-sunken, #f1f5f9);
}

.ex-hint {
  margin-left: auto;
  font-size: 11px;
  color: var(--fi-text-muted, #6c757d);
}

.ex-chart-card { margin-bottom: 16px; }
.ex-canvas-wrap { position: relative; height: 480px; }
@media (max-width: 767px) {
  .ex-canvas-wrap { height: 380px; }
}

.ex-bars-card { margin-bottom: 25px; }
.ex-bars { display: flex; flex-direction: column; gap: 4px; }

/* sits opposite the title and wraps under it on a narrow card, same as the
   hint it replaced */
.ex-bar-filters {
  margin-left: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ex-bar-filters .sc-chip:disabled {
  opacity: 0.45;
  cursor: default;
}
.ex-chip-count {
  font-variant-numeric: tabular-nums;
  opacity: 0.75;
  margin-left: 2px;
}
.ex-bars-empty {
  font-size: 12.5px;
  color: var(--fi-text-muted, #6c757d);
  padding: 18px 0 4px;
  margin: 0;
}

.ex-bars-intro {
  font-size: 12.5px;
  color: var(--fi-text-muted, #6c757d);
  margin: 0 0 12px;
  max-width: 88ch;
}
/* a miniature of the marker itself, so the sentence can point at the thing
   rather than describe it */
.ex-tick-inline {
  display: inline-block;
  width: 2px;
  height: 11px;
  background: var(--fi-text, #1a2733);
  opacity: 0.75;
  vertical-align: -1px;
  margin: 0 3px;
}

/* same grid as a bar row, so the labels sit over the columns they name */
.ex-bar-head {
  display: grid;
  grid-template-columns: 150px 1fr 68px 56px 68px 116px;
  align-items: end;
  gap: 10px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--fi-text-muted, #6c757d);
  padding-bottom: 6px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--fi-line, #dee2e6);
}
.ex-bar-head .ex-bar-score,
.ex-bar-head .ex-bar-miles,
.ex-bar-head .ex-bar-delta { color: var(--fi-text-muted, #6c757d); }

/* one grid template on the row, so the columns line up down the whole list
   without each cell carrying its own width */
.ex-bar-row {
  display: grid;
  grid-template-columns: 150px 1fr 68px 56px 68px 116px;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  cursor: pointer;
  padding: 2px 0;
}
.ex-bar-row:hover .ex-bar-name { text-decoration: underline; }
.ex-bar-name {
  color: var(--fi-link, #007bff);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ex-bar-score {
  text-align: right;
  font-family: var(--fi-font-mono, monospace);
  font-variant-numeric: tabular-nums;
  color: var(--fi-text-muted, #6c757d);
}
.ex-over-rate { color: #C1352F; font-weight: 600; }
.ex-bar-head .ex-over-rate { color: inherit; }
.ex-bar-track {
  position: relative;
  height: 13px;
  background: var(--fi-surface-sunken, #eef2f6);
  border-radius: 3px;
  min-width: 60px;
}
.ex-bar-fill {
  position: absolute;
  left: 0; top: 0;
  height: 13px;
  border-radius: 3px;
  opacity: 0.9;
}
/* the risk-neutral marker. contrast against the fill, not the track, since it
   usually sits on top of the bar */
.ex-bar-tick {
  position: absolute;
  top: -2px;
  height: 17px;
  width: 2px;
  background: var(--fi-text, #1a2733);
  opacity: 0.75;
}
.ex-bar-miles, .ex-bar-delta {
  text-align: right;
  font-family: var(--fi-font-mono, monospace);
  font-variant-numeric: tabular-nums;
}
.ex-bar-miles { color: var(--fi-text-muted, #6c757d); }
.ex-pos { color: #C1352F; }
.ex-neg { color: #1E7B3C; }
.ex-chip {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 3px;
  white-space: nowrap;
}
.ex-chip--over { background: rgba(193, 53, 47, 0.13); color: #C1352F; }
.ex-chip--under { background: rgba(30, 123, 60, 0.13); color: #1E7B3C; }
.ex-legend-note {
  font-size: 11.5px;
  color: var(--fi-text-muted, #6c757d);
  margin: 12px 0 0;
}

@media (max-width: 767px) {
  .ex-bar-row, .ex-bar-head { grid-template-columns: 110px 1fr 58px 48px; }
  .ex-bar-delta, .ex-bar-flag { display: none; }
  .ex-bar-head > :nth-child(5), .ex-bar-head > :nth-child(6) { display: none; }
}
</style>
